/**
 * Which trades a trade cannot actually promise.
 *
 * Most of what a trade moves survives it. A shiny sent is a shiny received; so
 * is a size, a form, a Mega-capable species. Two things are different, and the
 * trade surfaces were quietly presenting them as if they were not:
 *
 *   LUCKY is conferred BY the trade, not carried through it. Neither trainer
 *   holds a Lucky before the swap, and whether one comes out of it is chance
 *   unless the two are Lucky Friends.
 *
 *   100% is re-rolled. A trade re-rolls the IVs of the Pokémon that changes
 *   hands, so nobody is sending a hundo and nobody can promise one. The best
 *   any trade does is raise the FLOOR the re-roll lands above, which shortens
 *   the odds and never removes them.
 *
 * So a Lucky or 100% trade row is a plan for an outcome, where every other row
 * is a plan for an object. Saying so on the row is the honest version, and it
 * is also the useful one: it is the difference between a trainer feeling
 * cheated by a swap and understanding what they agreed to.
 *
 * This is the same fact the search string is built on. See tradeSearchString,
 * which will not search `lucky` or `4*` for exactly this reason.
 */

/** Variants a trade can only ever produce by chance. */
const CHANCE_ONLY = new Set(["lucky", "perfect", "shundo", "shinyLucky"]);

export function isChanceOnlyVariant(variant: string): boolean {
  return CHANCE_ONLY.has(variant);
}

/**
 * Why this trade is a chance, in one line a trainer can act on.
 *
 * Named per variant rather than given one generic sentence: "the IVs re-roll"
 * and "you both have to be Lucky Friends" lead to completely different
 * decisions, and a shared blur would help with neither.
 */
export function chanceOnlyReason(variant: string): string | null {
  switch (variant) {
    case "lucky":
      return "Lucky comes from the trade itself, so it is a chance unless you are Lucky Friends.";
    case "perfect":
      return "A trade re-rolls IVs, so a 100% is a chance. Trading raises the floor it rolls above, it does not guarantee the result.";
    case "shundo":
      return "The shiny carries across, but the 100% is re-rolled by the trade, so that half is a chance.";
    case "shinyLucky":
      return "The shiny carries across. Lucky comes from the trade, so it is a chance unless you are Lucky Friends.";
    default:
      return null;
  }
}
